import { Spinner, mergeClasses } from "@fluentui/react-components";
import { getProjectIqWorkspaceDocuments } from "apps/project-iq/api";
import {
  ANALYTICS_VALID_FILE_EXTENSIONS,
  getFileTypeInfo,
} from "apps/project-iq/utils/fileUtils";
import { DocumentIcon } from "icons/DocumentIcon";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useWorkspaceFilePickerStyles } from "./WorkspaceFilePicker.styles";
import { IWorkspaceFilePickerProps } from "./WorkspaceFilePicker.types";

export const isAnalyticsEligibleFile = (filename: string): boolean => {
  const lowerFilename = filename.toLowerCase();
  return ANALYTICS_VALID_FILE_EXTENSIONS.some((extension) =>
    lowerFilename.endsWith(extension),
  );
};

export const WorkspaceFilePicker = ({
  workspaceId,
  selectedFilename,
  onSelectionChange,
}: IWorkspaceFilePickerProps): JSX.Element => {
  const styles = useWorkspaceFilePickerStyles();

  const { data: documents, isLoading } = useQuery<IWorkspaceLibraryDocument[]>(
    ["workspace-documents", workspaceId],
    () => getProjectIqWorkspaceDocuments({ workspaceId }),
    { enabled: !!workspaceId },
  );

  const eligibleDocuments = useMemo(
    () =>
      (documents ?? []).filter((doc) =>
        isAnalyticsEligibleFile(doc.originalFilename),
      ),
    [documents],
  );

  const iconStyleMap: Record<string, string> = {
    pdf: styles.fileIconPdf,
    xls: styles.fileIconXls,
    ppt: styles.fileIconPpt,
    default: styles.fileIconDefault,
  };

  const handleSelect = useCallback(
    (doc: IWorkspaceLibraryDocument): void => {
      if (doc.filename === selectedFilename) return;
      onSelectionChange(doc);
    },
    [selectedFilename, onSelectionChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, index: number): void => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSelect(eligibleDocuments[index]);
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex =
          (index + delta + eligibleDocuments.length) % eligibleDocuments.length;
        const nextDoc = eligibleDocuments[nextIndex];
        handleSelect(nextDoc);
        const nextElement = document.getElementById(
          `workspace-file-picker-item-${nextDoc.filename}`,
        );
        nextElement?.focus();
      }
    },
    [eligibleDocuments, handleSelect],
  );

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading workspace files..." size="small" />
      </div>
    );
  }

  if (eligibleDocuments.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          No CSV or XLSX files are available in this workspace's library yet.
          Upload a data file to the Workspace Files tab first, then come back
          here to select it.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ul
        className={styles.list}
        role="radiogroup"
        aria-label="Select a workspace file to analyze"
      >
        {eligibleDocuments.map((doc, index) => {
          const isSelected = doc.filename === selectedFilename;
          const typeInfo = getFileTypeInfo(doc.originalFilename);
          return (
            <li
              key={doc.filename}
              id={`workspace-file-picker-item-${doc.filename}`}
              className={mergeClasses(
                styles.item,
                isSelected && styles.itemSelected,
              )}
              role="radio"
              aria-checked={isSelected}
              aria-label={doc.originalFilename}
              tabIndex={isSelected || index === 0 ? 0 : -1}
              onClick={() => handleSelect(doc)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span
                className={mergeClasses(
                  styles.radioDot,
                  isSelected && styles.radioDotSelected,
                )}
                aria-hidden="true"
              >
                {isSelected && <span className={styles.radioDotInner} />}
              </span>
              <span
                className={mergeClasses(
                  styles.fileIconWrapper,
                  iconStyleMap[typeInfo.style] ?? styles.fileIconDefault,
                )}
                aria-hidden="true"
              >
                <DocumentIcon size={18} />
              </span>
              <span className={styles.fileInfo}>
                <p className={styles.fileName}>{doc.originalFilename}</p>
                <p className={styles.fileMeta}>{typeInfo.label}</p>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
